'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics and Heart Attacks: What the 2026 Studies Found, and What Scientists Now Dispute"
      author="MicroPlastics Research Desk"
      date="July 17, 2026"
      readTime="12 min read"
      heroImage="/blog/heroes/health.webp"
      heroImageAlt="A stylized human heart and circulatory system representing cardiovascular research"
    >
      <BlogLastUpdated iso="2026-08-01" formatted="August 1, 2026" />

      <BlogTLDR>
        A study published in the <strong>European Heart Journal</strong> in
        July 2026 found that people who had just suffered a serious heart
        attack carried <strong>more micro- and nanoplastics in their coronary
        blood</strong>, and a wider variety of them, than patients with stable
        heart disease or people with healthy arteries. Plastics were detected
        in <strong>84%</strong> of the heart-attack group. It follows a 2024{' '}
        <em>New England Journal of Medicine</em> study that found plastic
        particles lodged in the artery plaque of 58% of patients, who went on
        to have <strong>4.5&times; the rate</strong> of heart attack, stroke,
        or death.
        {' '}
        <strong>What the headlines left out:</strong> within two weeks, six
        independent scientists put specific objections on the record. After
        statistical adjustment, <strong>smoking was the only independent
        predictor</strong>. Heart-attack patients receive intravenous lines
        that themselves shed plastic, before the blood was drawn. And two
        environmental chemists argue the method may be reading{' '}
        <strong>blood lipids as polyethylene</strong>, a false positive that
        has now been flagged in 18 earlier blood studies. The exposure cuts
        below are still worth making. The number in the headline is not yet
        worth believing.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>The July 2026 study (Paolisso et al., European Heart Journal) sampled peripheral and coronary blood from 61 people and found the highest micro/nanoplastic load in the heart-attack (STEMI) group, with particles detected in 84% of them.</>,
          <>It builds on Marfella et al. (NEJM, 2024), which found micro/nanoplastics in the carotid plaque of 58% of patients, linked to a 4.5&times; higher rate of heart attack, stroke, or death over ~34 months.</>,
          <>After adjustment, <strong>smoking was the only independent predictor</strong> of blood plastics. All smokers with higher air-pollution exposure had detectable plastics, versus 12.5% of non-smokers with lower exposure.</>,
          <>STEMI patients are treated with IV infusions that shed plastic into the blood. Because samples were taken during treatment, the heart attack may partly be causing the reading rather than the other way round.</>,
          <>Lipids in blood produce a false positive for polyethylene, the most common plastic. Rauert (Univ. of Queensland) found 18 previous blood studies did not account for this, and the same objection has been raised against this one.</>,
          <>The exposure cuts are unchanged and still cheap: filter your drinking water, stop heating food in plastic, cut bottled water. Not supplements, not detoxes.</>,
        ]}
      />

      <RiskQuizInlineNudge source="blog_inline_heart_attacks" />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What the new July 2026 study actually found
      </h2>
      <p>
        In mid-July 2026, researchers led by Professor Emanuele Barbato at
        Sapienza University of Rome published a study in the{' '}
        <em>European Heart Journal</em> (Paolisso et al.) that did something
        simple and pointed: they measured micro- and nanoplastics in the blood
        of people at three different stages of heart health and compared the
        loads. The paper appeared online on July 14; the European Society of
        Cardiology publicised it on July 15, and the wire coverage followed
        within 48 hours.
      </p>
      <p>
        The 61 participants fell into three groups: people admitted with a{' '}
        <strong>STEMI</strong> (an ST-elevation myocardial infarction, the
        most serious kind of heart attack), people with{' '}
        <strong>chronic coronary syndrome</strong> (stable, long-standing heart
        disease), and people with <strong>no coronary artery disease</strong>{' '}
        at all. Crucially, the team sampled not just peripheral blood but{' '}
        <strong>coronary blood</strong>, drawn from the heart&apos;s own
        circulation during angiography, which is what made the study novel.
        The heart-attack group had both the highest concentration and the
        widest variety of plastic polymers. Particles were detected in{' '}
        <strong>84%</strong> of the STEMI group. The healthy-artery group had
        the least.
      </p>
      <p>
        The particles also travelled with two other things: raised{' '}
        <strong>inflammatory markers</strong> and higher{' '}
        <strong>PM2.5</strong> (fine particulate air pollution) exposure. That
        combination is what the authors lean on for a mechanism, and it is
        also, as the next section explains, the reason several cardiologists
        think the plastics may be a passenger rather than a driver.
      </p>
      <p>
        One detail matters more than the headline: the people who both{' '}
        <strong>smoked</strong> and lived with higher air-pollution exposure
        almost all had plastics in their blood, while only about 12.5% of
        non-smokers with lower pollution exposure did. Smokers were roughly six
        times more likely to test positive. Read carefully, that is not a
        supporting detail. In the adjusted analysis,{' '}
        <strong>smoking was the only independent predictor</strong> of blood
        plastics that survived, which means the cleanest reading of this study
        may be a study about smoking.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why this landed so hard: the 2024 NEJM study it echoes
      </h2>
      <p>
        The reason this new paper got wall-to-wall coverage is that it lines up
        with the most alarming microplastics finding to date. In March
        2024, Marfella et al. published a study in the{' '}
        <em>New England Journal of Medicine</em> that examined the fatty plaque
        surgically removed from the carotid arteries of 257 patients. They found
        micro- and nanoplastics embedded in the plaque of{' '}
        <strong>58% of them</strong>.
      </p>
      <p>
        Then they followed those patients for about 34 months. The people with
        plastics in their artery plaque had a <strong>4.5 times higher rate</strong>{' '}
        of the combined outcome of heart attack, stroke, or death from any cause
        (hazard ratio 4.53) than the people whose plaque was plastic-free. That
        is a very large effect for an environmental exposure, and it is the study
        that turned microplastics from an ecological worry into a
        cardiovascular one.
      </p>
      <p>
        The July 2026 study doesn&apos;t replace the NEJM one, it complements it
        from a different angle. NEJM looked at plastic <em>in the plaque</em>;
        the new study looks at plastic <em>circulating in blood</em> and shows
        it tracks with how sick the heart is. Two independent teams, two
        different tissues, same direction. That consistency is what makes
        researchers take it seriously, even while they caution against
        over-reading it.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The three studies at a glance
      </h2>

      <BlogComparisonTable
        caption="The cardiovascular microplastics evidence, 2024–2026, with the challenges against each"
        columns={[
          { key: 'study', label: 'Study' },
          { key: 'looked', label: 'What it measured' },
          { key: 'found', label: 'Headline finding' },
          { key: 'type', label: 'Evidence type' },
          { key: 'challenge', label: 'Status / challenge' },
        ]}
        rows={[
          { study: 'Marfella et al., NEJM (2024)', looked: 'Plastics in surgically removed carotid artery plaque, 257 patients', found: 'Plastics in 58% of plaques; those patients had 4.5× the rate of heart attack, stroke, or death over ~34 months', type: 'Prospective observational (association)', challenge: 'Strongest of the three: real tissue, real follow-up. Still observational, and the detection method is subject to the same false-positive debate.' },
          { study: 'Paolisso et al., Eur. Heart J. (July 2026)', looked: 'Micro/nanoplastics in peripheral and coronary blood of 61 people at 3 stages of heart health', found: 'STEMI patients had the highest load and variety, detected in 84%; healthy-artery group the least', type: 'Cross-sectional observational (association)', challenge: 'Six scientists on record with objections. Smoking was the only independent predictor after adjustment; IV lines during treatment are an untested confound.' },
          { study: 'Nihart / Campen et al., Nature Medicine (2025)', looked: 'Plastic accumulation in human organ tissue at autopsy', found: 'Micro/nanoplastics in nearly every brain, liver, and kidney sample; higher in more recent deaths', type: 'Descriptive (presence, not risk)', challenge: 'Formally contested. A November 2025 Matters Arising (Monikh et al.) in the same journal disputes the methodology. Treat the brain numbers as unsettled.' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What happened after the headlines: six scientists pushed back
      </h2>
      <p>
        This is the part that never made the news cycle, because it arrived
        after it. When the study was published, the UK and New Zealand{' '}
        <strong>Science Media Centres</strong> ran the standard practice of
        asking uninvolved specialists to comment on the record. Six did. None
        of them called the work fraudulent or bad, several called it valuable.
        But between them they raised four objections serious enough that the
        honest summary of this study is <em>not</em> the one you read in July.
      </p>

      <h3 className="font-display text-xl font-semibold text-ink mt-8">
        1. After adjustment, only smoking survived
      </h3>
      <p>
        Dr Vahitha Abdul Salam of Queen Mary University of London noted that
        the study &ldquo;remains relatively small (61 patients) and
        cross-sectional, so it cannot determine causation&rdquo; &mdash; and,
        more pointedly, that <strong>smoking emerged as the only independent
        predictor after adjustment</strong>. Every other candidate, including
        the plastics themselves, failed to hold up once the statistics
        accounted for the others. A study where the plastic signal collapses
        into the smoking signal is a much weaker result than
        &ldquo;microplastics linked to heart attacks.&rdquo;
      </p>

      <h3 className="font-display text-xl font-semibold text-ink mt-8">
        2. The IV line problem
      </h3>
      <p>
        Prof Alun Hughes of the UCL Institute of Cardiovascular Science raised
        the objection that is hardest to dismiss. Someone having a STEMI is
        rushed into a cath lab and given{' '}
        <strong>intravenous infusions through plastic tubing</strong>, and IV
        infusions are a known route for plastic particles to enter the
        bloodstream directly. The blood was sampled during that treatment. So
        the group with the most plastic in their blood is also the group that
        just received the most plastic-delivered medicine. That is not a
        subtle confound, and the study design cannot rule it out.
      </p>

      <h3 className="font-display text-xl font-semibold text-ink mt-8">
        3. Was it even plastic? The lipid false positive
      </h3>
      <p>
        The deepest objection is about measurement itself. Prof Kevin Thomas
        of the Queensland Alliance for Environmental Health Sciences put it
        bluntly: <strong>&ldquo;It is just as plausible that endogenous lipids
        in the blood are being misidentified as plastics.&rdquo;</strong>
      </p>
      <p>
        This is not a hunch. His Queensland colleague, environmental chemist{' '}
        <strong>Cassandra Rauert</strong>, published the underlying finding:
        &ldquo;Lipids and fats will give you a false positive for
        polyethylene. Lipids are made up of the same building blocks as
        polyethylene, so when we analyze them, they look identical.&rdquo;
        Polyethylene is the most commonly produced plastic on earth and
        therefore the one these studies most often report finding. Rauert
        identified <strong>18 previous blood microplastics studies</strong>{' '}
        that did not account for the problem: &ldquo;To our knowledge, these
        previous studies didn&apos;t know about this issue and haven&apos;t
        considered it.&rdquo;
      </p>
      <p>
        In other words, an unknown fraction of the human-blood microplastics
        literature may be measuring body fat. That does not mean the plastics
        aren&apos;t there. It means the field does not yet have a method
        reliable enough to tell you how much.
      </p>

      <h3 className="font-display text-xl font-semibold text-ink mt-8">
        4. This is now a field-wide reckoning
      </h3>
      <p>
        The scrutiny is not confined to one paper. Seven studies in this area
        have been formally challenged in scientific journals, and the{' '}
        <em>Nature Medicine</em> brain-accumulation study &mdash; the source of
        the widely repeated &ldquo;a spoon&apos;s worth of plastic in your
        brain&rdquo; claim, and a study we cite in the table above &mdash; drew
        a formal <em>Matters Arising</em> rebuttal from an international group
        led by Fazel Monikh in November 2025 disputing its methodology. There
        is still <strong>no internationally standardised method</strong> for
        sampling, extracting, identifying, and quantifying these particles,
        which is why two labs can analyse similar samples and disagree wildly.
      </p>
      <p>
        Rauert, notably, is not a blanket sceptic. She is confident about a
        different measurement: &ldquo;Analysis of stool samples has shown a
        wide variety of plastic particles in them.&rdquo; We are definitely
        eating plastic and definitely excreting it. What remains genuinely
        unresolved is how much stays, where it goes, and what it does when it
        gets there.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Does this prove microplastics cause heart attacks? No, and here&apos;s why
      </h2>
      <p>
        Set the measurement dispute aside for a moment and assume the particles
        are real and correctly identified. Even then, everything above is{' '}
        <strong>correlation</strong>. Sick hearts have more blood plastic, and
        plaque with plastic in it is more dangerous, but no study has shown that{' '}
        <em>removing</em> plastic lowers risk, because that experiment is
        essentially impossible to run on people. Dr Ria Devereux of the
        University of East London stated the bottom line plainly: the study
        &ldquo;does not show that micro and nanoplastics cause heart
        attacks.&rdquo; Three structural caveats sit underneath that:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Small samples.</strong> 61 people is enough to detect a difference but not to rule out chance or untangle causes. Neither patients nor analysts were blinded, and Prof Hughes flagged patient selection as a further source of bias.</li>
        <li><strong>Confounding.</strong> Plastic exposure rides along with smoking, air pollution, ultra-processed diet, and lower income, all independent heart-disease drivers. In this study the adjusted analysis left smoking standing alone. Some of the &ldquo;plastic effect&rdquo; may be those other things wearing a plastic costume.</li>
        <li><strong>Reverse direction is possible.</strong> Beyond the IV-line issue, a damaged, inflamed cardiovascular system might trap or retain particles differently than a healthy one, so the heart attack could partly drive the reading rather than the other way around.</li>
      </ul>
      <p>
        None of that makes the finding meaningless. A plausible biological
        mechanism plus repeated human associations across independent teams is
        exactly how a risk factor first shows up before the causal proof
        arrives, it is the stage tobacco and air pollution were once at. But
        that pattern only counts if the underlying measurements are sound, and
        that is precisely what is now in dispute. The honest position in August
        2026 is narrower than the one we took in July: microplastics are a{' '}
        <strong>plausible but unconfirmed cardiovascular risk factor</strong>,
        resting on a measurement method the field has not yet standardised.
        Reduce exposure because it is cheap, low-regret, and supported by
        clearer evidence elsewhere &mdash; not because this study settled
        anything.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The plausible mechanism
      </h2>
      <p>
        Why would plastic particles hurt arteries? The leading hypothesis is{' '}
        <strong>chronic inflammation</strong>. Nanoplastics are small enough to
        cross from the gut and lungs into the bloodstream, and lab and animal
        work shows they can lodge in the vessel wall, be engulfed by immune
        cells, and trigger a low-grade inflammatory response, the same process
        that destabilizes atherosclerotic plaque and precipitates heart
        attacks. The particles may also ferry adsorbed chemicals (bisphenols,
        phthalates) that have their own cardiovascular signals. This is
        mechanistically coherent, which is why cardiologists aren&apos;t
        dismissing the human data outright.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What actually reduces your exposure (in order of payoff)
      </h2>
      <p>
        If you take one action from this study, make it a real one. Exposure
        reduction is where the evidence is strongest and the cost is lowest, and
        the ranking is not what the supplement ads suggest.
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Filter your drinking water.</strong> A certified reverse-osmosis or microplastic-rated pitcher removes the bulk of waterborne particles. See our{' '}
          <Link href="/blog/microplastics-water-filters-compared" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">water filters compared</Link> guide.
        </li>
        <li><strong>Stop heating food in plastic.</strong> Heat is the single biggest multiplier of microplastic release. Never microwave in plastic, and don&apos;t pour boiling liquid into it, glass or stainless instead.</li>
        <li><strong>Cut bottled water.</strong> PNAS (2024) found roughly 240,000 plastic particles per liter in bottled water, an order of magnitude more than most tap. It is one of the largest single ingestion sources.</li>
        <li><strong>Ditch plastic food storage for glass or steel,</strong> especially for hot, fatty, or acidic foods.</li>
        <li><strong>Address the confounders that ride with plastic:</strong> don&apos;t smoke, and reduce indoor air pollution, the study suggests these amplify blood-plastic levels dramatically.</li>
      </ul>
      <p>
        What&apos;s <em>not</em> on the list: &ldquo;microplastic detox&rdquo;
        supplements, cleanses, or postbiotics. There is no human evidence any of
        them clear plastics from tissue, see our honest{' '}
        <Link href="/blog/7-day-microplastic-detox-challenge" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">microplastics detox</Link>{' '}
        breakdown before spending money on one.
      </p>

      <BlogWhatTheAppChecks
        items={[
          <>The 0&ndash;100 microplastic risk score of your bottled-water, food-storage, and kitchen products from a photo or barcode.</>,
          <>Whether your water filter is certified to actually remove micro/nanoplastics, or just tastes better.</>,
          <>A lower-exposure swap in the same category for anything that scores badly.</>,
          <>Your household exposure trend over time, so you can see whether the swaps are working month over month.</>,
        ]}
      />

      <BlogAppCTA
        headline="Turn the headline into a 10-minute exposure cut"
        sub="You can&apos;t control the study, but you can control your kitchen. Scan your water, bottles, and storage with MicroPlastics to see which items drive most of your exposure, and the cleaner swap for each."
        buttonLabel="Scan my kitchen"
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Who should care most
      </h2>
      <p>
        The signal is strongest for people who already carry cardiovascular
        risk: <strong>smokers, people in high air-pollution areas, and anyone
        with existing heart disease, high blood pressure, or a strong family
        history</strong>. For that group, exposure reduction is a cheap add-on
        to the things that matter far more, not smoking, controlling blood
        pressure and cholesterol, exercise, and diet. Microplastics are a
        rational thing to reduce; they are not a reason to ignore the
        established basics, which still dwarf any plastic effect measured so far.
      </p>

      <p>
        For the deeper science on how these particles get into artery walls, see
        our explainer on{' '}
        <Link href="/blog/microplastics-in-arterial-plaque" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in arterial plaque
        </Link>
        , and on where they end up in the body,{' '}
        <Link href="/blog/microplastics-in-human-blood" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in human blood
        </Link>{' '}
        and{' '}
        <Link href="/blog/how-long-do-microplastics-stay-in-your-body" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          how long microplastics stay in your body
        </Link>
        .
      </p>

      <BlogFAQ
        items={[
          {
            question: 'Do microplastics cause heart attacks?',
            answer:
              'Not proven, and the evidence got weaker rather than stronger after July 2026. Two studies found that people with heart attacks or high-risk artery plaque carry more micro/nanoplastics, but these are associations, not proof of cause. In the 2026 study, smoking was the only independent predictor after statistical adjustment. Several scientists have also questioned whether the particles were correctly identified as plastic at all. The honest read: a plausible but unconfirmed risk factor worth reducing cheaply, not a proven cause.',
          },
          {
            question: 'What did the July 2026 European Heart Journal study find?',
            answer:
              'Paolisso et al. measured micro- and nanoplastics in the peripheral and coronary blood of 61 people. Those who had just had a serious heart attack (STEMI) had the highest levels and widest variety of plastics, detected in 84% of them; people with healthy arteries had the least. Particles tracked with raised inflammatory markers, PM2.5 air-pollution exposure, and smoking. Smokers were about 6 times more likely to have detectable blood plastics.',
          },
          {
            question: 'Have scientists criticised the 2026 heart attack study?',
            answer:
              'Yes. Six independent researchers commented via the Science Media Centres. Dr Vahitha Abdul Salam (QMUL) noted smoking was the only independent predictor after adjustment. Prof Alun Hughes (UCL) pointed out that STEMI patients receive intravenous infusions that themselves shed plastic into blood before sampling. Prof Kevin Thomas (Univ. of Queensland) said it is "just as plausible that endogenous lipids in the blood are being misidentified as plastics." Dr Ria Devereux (UEL) said the study does not show that plastics cause heart attacks.',
          },
          {
            question: 'Can blood tests reliably detect microplastics?',
            answer:
              'Not yet, reliably. Environmental chemist Cassandra Rauert found that lipids and fats in blood produce a false positive for polyethylene, the most commonly produced plastic, because they share the same chemical building blocks and "look identical" under analysis. She identified 18 earlier blood microplastics studies that did not account for this. There is still no internationally standardised method for detecting and quantifying these particles, which is why labs disagree. Stool analysis is currently considered more trustworthy.',
          },
          {
            question: 'What was the 2024 NEJM microplastics heart study?',
            answer:
              'Marfella et al. (New England Journal of Medicine, 2024) analyzed carotid artery plaque removed from 257 patients and found micro/nanoplastics in 58% of them. Over ~34 months of follow-up, those patients had a 4.5 times higher rate of heart attack, stroke, or death than patients whose plaque had no detectable plastics.',
          },
          {
            question: 'How do I reduce microplastics to protect my heart?',
            answer:
              "The highest-payoff steps: filter your drinking water with a certified microplastic-rated filter, stop heating or microwaving food in plastic, cut bottled water (about 240,000 particles per liter), and switch food storage to glass or stainless steel. Don't bother with detox supplements, there's no evidence they clear plastics from the body.",
          },
          {
            question: 'Should I be worried about microplastics in my blood?',
            answer:
              "Concerned enough to act cheaply, not enough to panic. The cardiovascular associations are real but unproven as cause, and the measurement methods behind them are actively contested. Reducing exposure is inexpensive and low-regret, so it is worth doing regardless of how the science resolves. It should sit alongside, not replace, the established heart-health basics: not smoking, blood pressure and cholesterol control, exercise, and diet, which still dwarf any plastic effect measured so far.",
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Paolisso P, Barbato E, et al.',
            year: 2026,
            title: 'Micro- and nano-plastics in the coronary circulation and air pollution exposure in ischaemic heart disease presentation (doi:10.1093/eurheartj/ehag447)',
            publisher: 'European Heart Journal',
            url: 'https://doi.org/10.1093/eurheartj/ehag447',
          },
          {
            authors: 'Hughes A, Abdul Salam V, Devereux R, Thomas K, Palanisami T, Douwes J',
            year: 2026,
            title: 'Expert reaction to micro- and nano-plastics in the coronary circulation and air pollution exposure in ischaemic heart disease presentation',
            publisher: 'Science Media Centre (UK)',
            url: 'https://www.sciencemediacentre.org/expert-reaction-to-micro-and-nano-plastics-in-the-coronary-circulation-and-air-pollution-exposure-in-ischaemic-heart-disease-presentation/',
          },
          {
            authors: 'Rauert C (interviewed by Yale Environment 360)',
            year: 2026,
            title: 'What do we actually know about the microplastics inside us? (lipids give false positives for polyethylene; 18 prior blood studies affected)',
            publisher: 'Yale E360, University of Queensland',
            url: 'https://e360.yale.edu/features/cassandra-rauert-interview',
          },
          {
            authors: 'Marfella R, Prattichizzo F, Sardu C, et al.',
            year: 2024,
            title: 'Microplastics and Nanoplastics in Atheromas and Cardiovascular Events',
            publisher: 'New England Journal of Medicine 390:900–910',
            url: 'https://www.nejm.org/doi/full/10.1056/NEJMoa2309822',
          },
          {
            authors: 'Nihart AJ, Campen MJ, et al.',
            year: 2025,
            title: 'Bioaccumulation of microplastics in decedent human brains — note: formally contested by a Matters Arising response (Monikh et al., November 2025) disputing the methodology',
            publisher: 'Nature Medicine',
            url: 'https://www.nature.com/articles/s41591-024-03453-1',
          },
          {
            authors: 'Qian N, Gao X, Lang X, et al.',
            year: 2024,
            title: 'Rapid single-particle chemical imaging of nanoplastics by SRS microscopy (bottled water ~240,000 particles/L)',
            publisher: 'Proceedings of the National Academy of Sciences (PNAS)',
            url: 'https://www.pnas.org/doi/10.1073/pnas.2300582121',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
